import { Button, Form, FormInstance, Empty, List, Badge, Space, Typography, Card, Modal, Input } from "antd";
import { AnalyticEventParams } from "./analytic-event-params";
import { AnalyticEventsName } from "./analytic-events-name";
import { PlusCircleOutlined, BarChartOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import React, { useState, useCallback } from "react";
import { blue } from "@ant-design/colors";
import { AutoSizer, List as VirtualizedList, ListRowProps } from "react-virtualized";
import "react-virtualized/styles.css";

// Type definitions for better TypeScript support

interface AnalyticEvent {
    event_name: string;
    is_new?: boolean;
    event_params?: any[];
    is_editing?: boolean;
}

const { Text } = Typography;

export const AnalyticEventForm: React.FC<{ form: FormInstance<object> }> = ({ form }) => {
    const analyticEvents: AnalyticEvent[] = Form.useWatch("analytic_events", form) || [];
    const [editingEvent, setEditingEvent] = useState<{ index: number; name: number } | null>(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [draftEventData, setDraftEventData] = useState<any>(null);
    const [originalEventData, setOriginalEventData] = useState<any>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [searchText, setSearchText] = useState("");

    const handleEditEvent = useCallback(
        (index: number, name: number) => {
            const currentEventData = form.getFieldValue("analytic_events" as any)?.[name];
            setOriginalEventData(JSON.parse(JSON.stringify(currentEventData)));
            setDraftEventData(JSON.parse(JSON.stringify(currentEventData)));
            setEditingEvent({ index, name });
            setIsModalVisible(true);
            setHasChanges(false);
        },
        [form],
    );

    const handleModalClose = useCallback(() => {
        // Reset form về original data khi cancel
        if (editingEvent && originalEventData) {
            form.setFieldValue(["analytic_events", editingEvent.name], originalEventData);
        }
        setIsModalVisible(false);
        setEditingEvent(null);
        setDraftEventData(null);
        setOriginalEventData(null);
        setHasChanges(false);
    }, [editingEvent, originalEventData, form]);

    const handleSaveEditAnalyticEvent = useCallback(() => {
        // Lưu draft data vào form chính
        if (editingEvent && draftEventData) {
            form.setFieldValue(["analytic_events", editingEvent.name], draftEventData);
        }
        setIsModalVisible(false);
        setEditingEvent(null);
        setDraftEventData(null);
        setOriginalEventData(null);
        setHasChanges(false);
    }, [editingEvent, draftEventData, form]);

    // Function để update draft data và check changes
    const updateDraftData = useCallback(
        (newData: any) => {
            setDraftEventData(newData);
            const hasChanges = JSON.stringify(newData) !== JSON.stringify(originalEventData);
            setHasChanges(hasChanges);
        },
        [originalEventData],
    );

    return (
        <div className="h-[600px] flex flex-col">
            <Form.List name={"analytic_events"}>
                {(fields, { add, remove }) => {
                    // Filter fields based on search text
                    const filteredFields = searchText.trim()
                        ? fields.filter((field) => {
                              const eventName = analyticEvents[field.name]?.event_name || "";
                              return eventName.toLowerCase().includes(searchText.toLowerCase());
                          })
                        : fields;

                    // Render each row in the virtualized list
                    const renderRow = ({ index, key, style }: ListRowProps) => {
                        const field = filteredFields[index];
                        const { name } = field;
                        const eventName = analyticEvents[name]?.event_name || "";
                        const paramsCount = analyticEvents[name]?.event_params?.length || 0;
                        const isNew = analyticEvents[name]?.is_new;

                        return (
                            <div key={key} style={style} className="px-2">
                                <List.Item className="!p-0">
                                    <Card
                                        className="w-full my-1 shadow-sm hover:shadow-md transition-shadow"
                                        size="small"
                                        bodyStyle={{ padding: 0 }}
                                    >
                                        <div className="flex items-center justify-between p-3">
                                            <div className="flex items-center space-x-3 flex-1">
                                                <Badge
                                                    count={paramsCount}
                                                    size="small"
                                                    color={paramsCount > 0 ? blue[5] : "#d9d9d9"}
                                                    title={`${paramsCount} parameter${paramsCount !== 1 ? "s" : ""}`}
                                                />
                                                <div className="flex flex-col min-w-0 flex-1">
                                                    <Text strong className="text-sm truncate">
                                                        {eventName || (
                                                            <span className="text-gray-400 italic">
                                                                {isNew ? "New Event - Click to edit" : "Unnamed Event"}
                                                            </span>
                                                        )}
                                                    </Text>
                                                    {eventName && (
                                                        <Text type="secondary" className="text-xs">
                                                            {paramsCount} parameter{paramsCount !== 1 ? "s" : ""}
                                                        </Text>
                                                    )}
                                                </div>
                                            </div>
                                            <Space size="small">
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<EditOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // Find original index in unfiltered fields for edit event
                                                        const originalIndex = fields.findIndex((f) => f.name === name);
                                                        handleEditEvent(originalIndex, name);
                                                    }}
                                                    className="text-blue-500"
                                                    title="Edit event name"
                                                />
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={<DeleteOutlined />}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        remove(name);
                                                    }}
                                                    className="text-red-500"
                                                    title="Delete event"
                                                    danger
                                                />
                                            </Space>
                                        </div>
                                    </Card>
                                </List.Item>
                            </div>
                        );
                    };

                    // Fixed row height for consistent virtual scrolling
                    const ROW_HEIGHT = 80;

                    return (
                        <div className="flex flex-col h-full space-y-4 pr-2">
                            {/* Header with Search, Add Button and Summary - Sticky */}
                            <div className="sticky top-0 bg-white z-10 pb-4 border-b border-gray-200 space-y-3">
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center space-x-2">
                                        <BarChartOutlined className="text-gray-500" />
                                        <span className="text-sm text-gray-600">
                                            {searchText ? (
                                                <>
                                                    {filteredFields.length} of {fields.length} event
                                                    {fields.length !== 1 ? "s" : ""}{" "}
                                                    {filteredFields.length === 1 ? "matches" : "match"}
                                                </>
                                            ) : (
                                                <>
                                                    {fields.length} event{fields.length !== 1 ? "s" : ""} configured
                                                </>
                                            )}
                                        </span>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<PlusCircleOutlined />}
                                        onClick={() =>
                                            add({
                                                event_name: "",
                                                is_new: true,
                                                event_params: [],
                                            })
                                        }
                                        className="shadow-sm"
                                    >
                                        Add Event
                                    </Button>
                                </div>

                                {/* Search Input */}
                                <div className="w-full">
                                    <Input
                                        placeholder="Search events by name..."
                                        prefix={<SearchOutlined className="text-gray-400" />}
                                        value={searchText}
                                        onChange={(e) => setSearchText(e.target.value)}
                                        allowClear
                                        size="middle"
                                        className="w-full"
                                    />
                                </div>
                            </div>

                            {/* Events List - Virtual Scrollable */}
                            <div className="flex-1 min-h-0 bg-gray-50 rounded-md">
                                {fields.length === 0 ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description="No analytic events configured"
                                        className="py-8"
                                    />
                                ) : filteredFields.length === 0 ? (
                                    <Empty
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                        description={
                                            <div className="space-y-1">
                                                <div>No events match your search</div>
                                                <div className="text-xs text-gray-400">
                                                    Try searching for "{searchText}" or clear the search filter
                                                </div>
                                            </div>
                                        }
                                        className="py-8"
                                    />
                                ) : (
                                    <AutoSizer>
                                        {({ height, width }) => (
                                            <VirtualizedList
                                                width={width}
                                                height={height}
                                                rowCount={filteredFields.length}
                                                rowHeight={ROW_HEIGHT}
                                                rowRenderer={renderRow}
                                                overscanRowCount={5}
                                                className="virtualized-list"
                                            />
                                        )}
                                    </AutoSizer>
                                )}
                            </div>
                        </div>
                    );
                }}
            </Form.List>

            {/* Edit Event Modal */}
            <Modal
                title="Edit Event"
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={800}
                destroyOnHidden
                centered
            >
                {editingEvent && draftEventData && (
                    <div className="space-y-4">
                        <AnalyticEventsName
                            name={editingEvent.name}
                            form={form}
                            draftData={draftEventData}
                            onDraftChange={updateDraftData}
                        />
                        <AnalyticEventParams
                            form={form}
                            name={editingEvent.name}
                            draftData={draftEventData}
                            onDraftChange={updateDraftData}
                        />
                        <div className="flex justify-end space-x-2 pt-4 border-t">
                            <Button onClick={handleModalClose}>Cancel</Button>
                            <Button type="primary" onClick={handleSaveEditAnalyticEvent} disabled={!hasChanges}>
                                Save Changes
                            </Button>
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};
